/** Cells with no editing state of their own: a checkbox, plain text, and one that opens an editor. */

import React from 'react';
import { Checkbox } from '@roger/ui-components';
import { Loader2, Maximize2 } from 'lucide-react';
import { isOnOpenIcon, openIconProps } from '../dom';
import { CELL_CLASS } from './cellClass';

export const BoolCell: React.FC<{
  value: boolean;
  disabled?: boolean;
  label: string;
  onCommit: (value: boolean) => void;
}> = ({ value, disabled, label, onCommit }) => (
  <div className='flex h-9 w-full items-center justify-center hover:bg-muted/60'>
    <Checkbox
      checkboxSize='sm'
      checked={value}
      disabled={disabled}
      aria-label={label}
      onCheckedChange={checked => onCommit(checked === true)}
    />
  </div>
);

/** A read-only cell: one line, truncated, with the full text on hover. */
export const ReadCell: React.FC<{
  children: React.ReactNode;
  title?: string;
  muted?: boolean;
}> = ({ children, title, muted }) => (
  <div
    className={`${CELL_CLASS} ${muted ? 'text-muted-foreground' : ''}`}
    title={title}
  >
    <span className='truncate'>{children}</span>
  </div>
);

/**
 * A cell that opens an editor. A click selects it; a double click, Enter (a
 * click from code, detail 0) or the open icon opens it.
 */
export const OpenCell: React.FC<{
  text: string;
  title?: string;
  muted?: boolean;
  disabled?: boolean;
  /** Shows a spinner in place of the open icon. */
  loading?: boolean;
  onOpen: () => void;
}> = ({ text, title, muted, disabled, loading, onOpen }) => {
  if (disabled) {
    return (
      <ReadCell title={title} muted={muted}>
        {text}
      </ReadCell>
    );
  }
  return (
    <button
      type='button'
      title={title}
      className={`${CELL_CLASS} group gap-2 text-left ${muted ? 'text-muted-foreground' : ''}`}
      onClick={e => {
        if (e.detail === 0 || isOnOpenIcon(e.target)) onOpen();
      }}
      onDoubleClick={onOpen}
    >
      <span className='min-w-0 flex-1 truncate'>{text}</span>
      {loading ? (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground' />
      ) : (
        <span
          {...openIconProps}
          className='-my-1 -mr-1 flex shrink-0 cursor-pointer items-center rounded p-1 hover:bg-muted'
        >
          <Maximize2 className='h-3 w-3 text-muted-foreground/50 group-hover:text-muted-foreground' />
        </span>
      )}
    </button>
  );
};
